import { useState, useEffect, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import VoiceSearch from '../search/VoiceSearch';
import { searchApi } from '../../api/searchApi';

interface SearchBarProps {
  onSearch: (term: string) => void;
  placeholder?: string;
  defaultValue?: string;
}

const SearchBar = ({ onSearch, placeholder, defaultValue = '' }: SearchBarProps) => {
  const [searchTerm, setSearchTerm] = useState(defaultValue);
  const [isListening, setIsListening] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Outside click se dropdown band karo
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce: 300ms baad API call
// Debounce useEffect mein — replace karo
useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    console.log('searchTerm changed:', searchTerm, 'length:', searchTerm.trim().length);

    if (searchTerm.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      console.log('⏱️ Debounce fired — calling API for:', searchTerm.trim());
      try {
        setLoading(true);
        const results = await searchApi.getSuggestions(searchTerm.trim());
        console.log('✅ Suggestions received:', results);
        setSuggestions(results);
        setShowDropdown(results.length > 0);
        setActiveIndex(-1);
      } catch(err) {
        console.error('❌ Suggestions error:', err); // Pehle silent tha!
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setShowDropdown(false);
      onSearch(searchTerm.trim());
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchTerm(suggestion);
    setShowDropdown(false);
    onSearch(suggestion);
  };

  // Keyboard navigation — arrow keys + enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };

  const handleVoiceResult = (text: string) => {
    setSearchTerm(text);
    setIsListening(false);
    onSearch(text);
  };

  return (
    <div ref={wrapperRef} className="w-full px-2 md:px-0 relative">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <input
            type="text"
            style={{ color: 'black' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
            placeholder={placeholder || "Search for medical codes or conditions..."}
            className="w-full px-4 md:px-6 py-2 md:py-4 text-sm md:text-lg border-2 border-gray-300 rounded-full focus:outline-none focus:border-primary-600 pl-10 md:pl-14 pr-24 md:pr-32"
            autoComplete="off"
          />
          {/* @ts-ignore */}
          <FaSearch className="absolute left-3 md:left-5 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg md:text-xl" />

          {/* Loading spinner */}
          {loading && (
            <div className="absolute right-36 top-1/2 transform -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          <div className="absolute right-2 md:right-3 top-1/2 transform -translate-y-1/2 flex space-x-1 md:space-x-2">
            <VoiceSearch
              onResult={handleVoiceResult}
              isActive={isListening}
              onListeningChange={setIsListening}
            />
            <button
              type="submit"
              className="bg-primary-600 text-white px-3 md:px-6 py-1 md:py-2 text-sm md:text-base rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap"
            >
              Search
            </button>
          </div>
        </div>
      </form>

      {/* ✅ Autocomplete Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              className={`w-full text-left px-5 py-3 text-sm flex items-center space-x-3 transition-colors ${
                activeIndex === index
                  ? 'bg-primary-50 text-primary-700'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              {/* @ts-ignore */}
              <FaSearch className="text-gray-400 text-xs flex-shrink-0" />
              {/* Bold the matching part */}
              <span dangerouslySetInnerHTML={{
                __html: suggestion.replace(
                  new RegExp(`(${searchTerm.trim()})`, 'gi'),
                  '<strong class="text-primary-600">$1</strong>'
                )
              }} />
            </button>
          ))}

          {/* Bottom hint */}
          <div className="px-5 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400 flex items-center space-x-1">
            <span>↑↓ navigate</span>
            <span className="mx-2">•</span>
            <span>Enter to select</span>
            <span className="mx-2">•</span>
            <span>Esc to close</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;